import { useCallback, useMemo } from "react";
import type {
  CustomFieldDataType,
  CustomFieldDefinition,
  SelectedCustomFields,
} from "@/types/custom-fields";
import type { EntityType } from "@/types/entity";
import { useFieldDefinitions } from "./api/use-field-definitions";
import { useTrackedSessionStorage } from "./use-tracked-browser-storage";

/**
 * Hook for managing selection of custom fields for a specific record type.
 * Persists selections to sessionStorage for tab-specific behavior.
 *
 * @param entity - The type of record to get field definitions for (e.g., "accounts" or "opportunities")
 * @param storageKey - Key to use for sessionStorage persistence
 * @returns Methods and state for managing selected fields
 */
export function useCustomFieldSelection(
  entity: EntityType,
  storageKey: string = `${entity}_custom_fields`,
) {
  const { data: fdData = [], isLoading, error } = useFieldDefinitions(entity);

  // Adapt unified defs ➔ legacy shape (custom only)
  const fieldDefs = useMemo<CustomFieldDefinition[]>(
    () =>
      fdData.map((fd) => ({
        id: fd.id,
        display_name: fd.display_name,
        data_type: fd.data_type as CustomFieldDataType,
        record_type: fd.record_type || "",
        value_enum: fd.enum_values || undefined,
      })),
    [fdData],
  );

  // Store selected field IDs in sessionStorage
  const [selectedFieldIds, setSelectedFieldIds] = useTrackedSessionStorage<
    string[]
  >(storageKey, []);

  // Create a map of selected field IDs to field definition objects
  const selectedFields = useMemo<SelectedCustomFields>(() => {
    const result: SelectedCustomFields = {};

    // Only include fields that actually exist in the definitions
    selectedFieldIds.forEach((fieldId) => {
      const field = fieldDefs.find((def) => def.id === fieldId);
      if (field) {
        result[fieldId] = field;
      }
    });

    return result;
  }, [fieldDefs, selectedFieldIds]);

  // Add a field to the selection
  const addField = useCallback(
    (fieldId: string) => {
      if (!selectedFieldIds.includes(fieldId)) {
        setSelectedFieldIds((prev) => [...(prev ?? []), fieldId]);
      }
    },
    [selectedFieldIds, setSelectedFieldIds],
  );

  // Remove a field from the selection
  const removeField = useCallback(
    (fieldId: string) => {
      setSelectedFieldIds((prev) => prev?.filter((id) => id !== fieldId) ?? []);
    },
    [setSelectedFieldIds],
  );

  // Toggle a field's selection status
  const toggleField = useCallback(
    (fieldId: string) => {
      if (selectedFieldIds.includes(fieldId)) {
        removeField(fieldId);
      } else {
        addField(fieldId);
      }
    },
    [selectedFieldIds, addField, removeField],
  );

  // Clear all selected fields
  const clearSelection = useCallback(() => {
    setSelectedFieldIds([]);
  }, [setSelectedFieldIds]);

  // Check if a field is selected
  const isFieldSelected = useCallback(
    (fieldId: string) => {
      return selectedFieldIds.includes(fieldId);
    },
    [selectedFieldIds],
  );

  // Get the list of selected field definitions
  const selectedFieldsList = useMemo<CustomFieldDefinition[]>(() => {
    return Object.values(selectedFields);
  }, [selectedFields]);

  // Extract field IDs for API requests
  const fieldIdsParam = useMemo(() => {
    return selectedFieldIds.join(",");
  }, [selectedFieldIds]);

  return {
    // State
    selectedFieldIds,
    selectedFields: selectedFields,
    selectedFieldsList,
    fieldIdsParam,
    availableFields: fieldDefs,
    isLoading,
    error,

    // Actions
    addField,
    removeField,
    toggleField,
    clearSelection,
    isFieldSelected,
  };
}

/**
 * Hook specifically for managing Account custom field selections.
 *
 * @returns Methods and state for managing selected account fields
 */
export function useAccountCustomFieldSelection() {
  return useCustomFieldSelection("accounts", "accounts::list::custom-fields");
}

/**
 * Hook specifically for managing Opportunity custom field selections.
 *
 * @returns Methods and state for managing selected opportunity fields
 */
export function useOpportunityCustomFieldSelection() {
  return useCustomFieldSelection(
    "opportunities",
    "opportunities::list::custom-fields",
  );
}

/**
 * Hook specifically for managing Person custom field selections.
 *
 * @returns Methods and state for managing selected person fields
 */
export function usePersonCustomFieldSelection() {
  return useCustomFieldSelection("persons", "persons::list::custom-fields");
}

/**
 * Hook specifically for managing Campaign custom field selections.
 *
 * @returns Methods and state for managing selected campaign fields
 */
export function useCampaignCustomFieldSelection() {
  return useCustomFieldSelection("campaigns", "campaigns::list::custom-fields");
}

/**
 * Hook specifically for managing webpage field selections.
 * Pre-configured with "webpages" record type and appropriate storage key.
 *
 * @returns Methods and state for managing selected webpage fields
 */
export function useWebpageCustomFieldSelection() {
  return useCustomFieldSelection("webpages", "webpages::list::custom-fields");
}
